import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  canAccessRouteWithPermissions,
  getDefaultRouteForPermissions,
  hasPermissionInList,
  type Permission,
} from "@shared/permissions";

export type AuthUser = {
  id: number;
  username: string;
  fullName: string;
  role: string;
  assignedFloor: string | null;
};

type AuthSession = {
  user: AuthUser;
  permissions: Permission[];
};

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
  canAccess: (path: string) => boolean;
  defaultRoute: string;
  permissions: Permission[];
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchSession(): Promise<AuthSession | null> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error("Failed to load session");
  const data = await res.json();
  return {
    user: data.user as AuthUser,
    permissions: (data.permissions ?? []) as Permission[],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const applySession = useCallback((session: AuthSession | null) => {
    setUser(session?.user ?? null);
    setPermissions(session?.permissions ?? []);
  }, []);

  const refreshSession = useCallback(async () => {
    const session = await fetchSession();
    applySession(session);
  }, [applySession]);

  useEffect(() => {
    fetchSession()
      .then(applySession)
      .catch(() => applySession(null))
      .finally(() => setIsLoading(false));
  }, [applySession]);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Login failed");
      }
      const data = await res.json();
      applySession({
        user: data.user as AuthUser,
        permissions: (data.permissions ?? []) as Permission[],
      });
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
    applySession(null);
  }, [applySession]);

  const value = useMemo<AuthContextValue>(() => {
    return {
      user,
      isLoading,
      login,
      logout,
      refreshSession,
      can: (permission) =>
        user ? hasPermissionInList(permissions, permission) : false,
      canAccess: (path) =>
        user ? canAccessRouteWithPermissions(permissions, path) : false,
      defaultRoute: user ? getDefaultRouteForPermissions(permissions) : "/login",
      permissions,
    };
  }, [user, permissions, isLoading, login, logout, refreshSession]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
